'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { TickerResolved } from './types';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { useSubstitute } from '@/lib/render/RenderContextProvider';
import { substituteHref } from '@/lib/render/substituteHref';

export const TickerComponent = ({
  resolved,
  className,
}: {
  resolved: TickerResolved;
  /**
   * Extra classes from a campaign variant wrapper. Variants B and C use it for
   * their top margin (SH-415); variant A passes nothing and is unchanged.
   */
  className?: string;
}) => {
  const t = useTranslations('common');
  const sub = useSubstitute();
  const storageKey = `ticker-dismissed:${resolved.id ?? resolved.message}`;
  const [dismissed, setDismissed] = useState(false);

  if (
    // The Standort's partner switched it off, or the template left the message
    // empty. Either way there is nothing to show — and the ticker reserves
    // layout height on mobile (`max-lg:fixed`), so an empty one is not free.
    resolved.hidden ||
    !resolved.message?.trim() ||
    dismissed ||
    (typeof sessionStorage !== 'undefined' &&
      Boolean(sessionStorage.getItem(storageKey)))
  ) {
    return null;
  }

  const dismiss = () => {
    sessionStorage.setItem(storageKey, '1');
    setDismissed(true);
  };

  const message = sub(resolved.message);
  const href = substituteHref(resolved.href, sub);
  const safeHref = href.match(/^(\/|#|https?:\/\/)/) ? href : undefined;

  return (
    <div
      className={classNames(
        'ticker-banner relative flex min-h-[var(--ticker-height)] items-center justify-center bg-[#d9edf7] pr-12 pl-6 text-center text-sm leading-snug font-bold text-[#31708f] max-lg:fixed max-lg:inset-x-0 max-lg:top-0 max-lg:z-50',
        className,
      )}
    >
      {safeHref ? (
        <a
          href={safeHref}
          onClick={() =>
            pushCmsEventTracking(resolved.tracking, { label: message })
          }
          className="mx-auto my-0 block max-w-4xl"
        >
          {message}
        </a>
      ) : (
        <p className="mx-auto my-0 max-w-4xl">{message}</p>
      )}
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('close')}
        className="hover:text-primary/50 absolute top-1/2 right-5 -translate-y-1/2 text-2xl leading-none text-gray-400/50"
      >
        &times;
      </button>
    </div>
  );
};
